import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  EXPERIENCE_LABELS,
  type Experience,
  type Search,
} from "@/lib/types";

export interface SearchFormValues {
  puesto: string;
  experience: Experience;
  rubro: string;
  description: string;
  salary_min: string;          // string para no pelearse con inputs vacíos
  salary_max: string;
  salary_currency: string;
  technologies: string[];
  stack: string;
  is_open: boolean;
}

const CURRENCIES = ["ARS", "USD", "EUR", "BRL"] as const;
const EXPERIENCES: Experience[] = [
  "junior",
  "semi_senior",
  "senior",
  "tech_lead",
  "manager",
  "cto",
  "ceo",
  "other",
];

export function emptySearchForm(): SearchFormValues {
  return {
    puesto: "",
    experience: "senior",
    rubro: "",
    description: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "ARS",
    technologies: [],
    stack: "",
    is_open: true,
  };
}

export function fromSearch(s: Partial<Search>): SearchFormValues {
  return {
    puesto: s.puesto ?? "",
    experience: (s.experience as Experience) ?? "senior",
    rubro: s.rubro ?? "",
    description: s.description ?? "",
    salary_min: s.salary_min != null ? String(s.salary_min) : "",
    salary_max: s.salary_max != null ? String(s.salary_max) : "",
    salary_currency: s.salary_currency ?? "ARS",
    technologies: s.technologies ?? [],
    stack: s.stack ?? "",
    is_open: s.is_open ?? true,
  };
}

interface Props {
  value: SearchFormValues;
  onChange: (next: SearchFormValues) => void;
}

export function SearchForm({ value, onChange }: Props) {
  const upd = <K extends keyof SearchFormValues>(
    k: K,
    v: SearchFormValues[K]
  ) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="puesto">Puesto</Label>
          <Input
            id="puesto"
            placeholder="Frontend Engineer"
            value={value.puesto}
            onChange={(e) => upd("puesto", e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="experience">Experiencia</Label>
          <Select
            value={value.experience}
            onValueChange={(v) => upd("experience", v as Experience)}
          >
            <SelectTrigger id="experience">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPERIENCES.map((e) => (
                <SelectItem key={e} value={e}>
                  {EXPERIENCE_LABELS[e]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rubro">Rubro de la búsqueda</Label>
        <Input
          id="rubro"
          placeholder="Fintech, Salud, SaaS B2B…"
          value={value.rubro}
          onChange={(e) => upd("rubro", e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">
          Opcional. Útil para diferenciar búsquedas distintas del mismo cliente.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="description">Descripción del puesto</Label>
        <Textarea
          id="description"
          placeholder="Responsabilidades, contexto del equipo, qué busca el cliente…"
          value={value.description}
          onChange={(e) => upd("description", e.target.value)}
          rows={6}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Rango salarial</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={value.salary_min}
            onChange={(e) => upd("salary_min", e.target.value)}
            className="flex-1"
            min={0}
          />
          <Input
            type="number"
            placeholder="Max"
            value={value.salary_max}
            onChange={(e) => upd("salary_max", e.target.value)}
            className="flex-1"
            min={0}
          />
          <Select
            value={value.salary_currency}
            onValueChange={(v) => upd("salary_currency", v)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Opcional. Bruto mensual por convención.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Tecnologías</Label>
        <TagInput
          values={value.technologies}
          onChange={(arr) => upd("technologies", arr)}
          placeholder="React, TypeScript, Node — separá con coma o Enter"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="stack">Stack / contexto técnico</Label>
        <Input
          id="stack"
          placeholder="React + Node + Postgres en Cloudflare"
          value={value.stack}
          onChange={(e) => upd("stack", e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">
          Una línea con el stack principal. Va en el post de LinkedIn.
        </p>
      </div>

      <label className="flex items-center gap-2.5 cursor-pointer pt-4 border-t border-border">
        <Checkbox
          checked={value.is_open}
          onCheckedChange={(v) => upd("is_open", !!v)}
        />
        <span className="text-sm">
          Búsqueda <strong>abierta</strong>
          <span className="text-muted-foreground ml-2 text-xs">
            (las cerradas se ocultan del flujo de invitación pero quedan en
            histórico)
          </span>
        </span>
      </label>
    </div>
  );
}

// =================== TAG INPUT ===================
function TagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  function commit(raw: string) {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !values.includes(s));
    if (parts.length > 0) {
      onChange([...values, ...parts]);
    }
    setDraft("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  function remove(t: string) {
    onChange(values.filter((v) => v !== t));
  }

  return (
    <div className="rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 focus-within:ring-offset-background focus-within:border-ring px-2 py-1.5 flex flex-wrap items-center gap-1.5">
      {values.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 bg-secondary text-foreground text-xs font-mono px-2 py-0.5 rounded"
        >
          {t}
          <button
            type="button"
            onClick={() => remove(t)}
            className="text-muted-foreground hover:text-destructive"
            aria-label={`Quitar ${t}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft && commit(draft)}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[140px] bg-transparent text-sm outline-none px-1 py-1"
      />
    </div>
  );
}

/** Helpers para serializar el form a payload de DB. */
export function toSearchInsertPayload(
  v: SearchFormValues,
  companyId: string
) {
  return {
    company_id: companyId,
    puesto: v.puesto.trim(),
    experience: v.experience,
    rubro: v.rubro.trim() || null,
    description: v.description.trim() || null,
    salary_min: v.salary_min ? Number(v.salary_min) : null,
    salary_max: v.salary_max ? Number(v.salary_max) : null,
    salary_currency: v.salary_currency,
    technologies: v.technologies,
    stack: v.stack.trim() || null,
    is_open: v.is_open,
  };
}

export function toSearchUpdatePayload(v: SearchFormValues) {
  return {
    puesto: v.puesto.trim(),
    experience: v.experience,
    rubro: v.rubro.trim() || null,
    description: v.description.trim() || null,
    salary_min: v.salary_min ? Number(v.salary_min) : null,
    salary_max: v.salary_max ? Number(v.salary_max) : null,
    salary_currency: v.salary_currency,
    technologies: v.technologies,
    stack: v.stack.trim() || null,
    is_open: v.is_open,
  };
}
